/**
 * The viewing-rooms screen's controller: `viewingRooms.model.ts` wired to
 * `fetchViewingRooms`, `fetchViewingRoomWorks`, `fetchViewingRoomVisitors`
 * and `fetchCollections` for the reads, and `createViewingRoom`,
 * `updateViewingRoom`, `setViewingRoomHidden`, `deleteViewingRoom` and
 * `setMyHandle` for the writes.
 *
 * It owns what the model cannot: the stage (browsing, a form, a room just
 * created), the draft inside it, the handle gate, the visit list and preview
 * held beside the room id they belong to, the "Link copied" timer, the
 * two-press delete, and the reload nonce. The model decides what a room row
 * says, what the form may send, and which screen the address bar claims.
 *
 * The work picker inside the form has a controller of its own —
 * `useWorkPickerController.ts` — but whether it is OPEN is this hook's, on the
 * stage, because a picker cannot be open over a room that is not being made.
 */

import { normaliseHandle } from "@ondo/shared";
import type {
  CollectionSummary,
  RoomVisitor,
  ViewingRoomSummary,
  ViewingRoomWork,
} from "@ondo/shared";
import { useCallback, useEffect, useRef, useState } from "react";

import {
  createViewingRoom,
  deleteViewingRoom,
  fetchCollections,
  fetchViewingRoomVisitors,
  fetchViewingRoomWorks,
  fetchViewingRooms,
  setMyHandle,
  setViewingRoomHidden,
  updateViewingRoom,
} from "../lib/api.ts";
import type { Choice } from "../lib/choices.ts";
import { useWorkThumbnails } from "../lib/useWorkThumbnails.ts";
import { COPIED_FOR_MS, PREVIEW_LIMIT, VIEWING_ROOMS_COPY } from "./viewingRooms.constants.ts";
import { labelFor } from "./workPicker.model.ts";
import type { SourceKind } from "./viewingRooms.constants.ts";
import {
  EMPTY_DRAFT,
  describeError,
  draftFromRoom,
  resolveRooms,
  roomCollectionOptions,
  toCreateRequest,
  toPreview,
  toUpdateRequest,
  toggleChoice,
  validateDraft,
  validateHandle,
  withSourceKind,
} from "./viewingRooms.model.ts";
import type { RoomDraft, RoomsView, SourceView, Stage } from "./viewingRooms.model.ts";

export interface ViewingRoomsController {
  view: RoomsView;
  /** The handle gate. */
  typeHandle: (value: string) => void;
  claimHandle: () => void;
  /** The create form. */
  startCreating: () => void;
  /** Opens the form over a room that already exists, prefilled from it. */
  startEditing: (roomId: string) => void;
  editDraft: (patch: Partial<RoomDraft>) => void;
  /** Opens and closes the work picker. */
  startPicking: () => void;
  stopPicking: () => void;
  chooseSource: (kind: SourceKind) => void;
  /** Takes the whole choice, so a paged-away selection can still be named. */
  toggleChosen: (choice: Choice) => void;
  submitDraft: () => void;
  cancelCreating: () => void;
  /** A room. */
  copyLink: (roomId: string, url: string) => void;
  hideRoom: (roomId: string, hidden: boolean) => void;
  askToDelete: (roomId: string | null) => void;
  deleteRoom: (roomId: string) => void;
}

/**
 * The collector's viewing rooms.
 *
 * Creating a room is a STAGE rather than an address, for the reason
 * `useCollectorController` gives: a screen gets a URL and a step in a form does
 * not. It is also settled by the parser — `parseRecord` is a strict uuid match,
 * so `/viewing-rooms/new` would be dropped and redirected before it rendered.
 *
 * A room itself IS an address, because it is a record: it has contents, a
 * password and a visitor list somebody will come back to. That is the same
 * argument the work record makes, and it does not weaken the rule that no share
 * control appears on a work — a room is a deliberate act of publication with a
 * lock, a hide switch and a log, made somewhere else.
 */
export function useViewingRoomsController(input: {
  handle: string | null;
  record: string | null;
  roomsHref: string;
  origin: string;
}): ViewingRoomsController {
  const [rooms, setRooms] = useState<ViewingRoomSummary[] | null>(null);
  const [failure, setFailure] = useState<string | null>(null);
  const [stage, setStage] = useState<Stage>({ kind: "browsing" });
  const [problem, setProblem] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [confirmingId, setConfirmingId] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const [handle, setHandle] = useState<string | null>(input.handle);
  const [handleDraft, setHandleDraft] = useState("");
  const [handleProblem, setHandleProblem] = useState<string | null>(null);

  const [collections, setCollections] = useState<CollectionSummary[] | null>(null);
  const [visits, setVisits] = useState<{ roomId: string; rows: RoomVisitor[] } | null>(
    null,
  );
  const [preview, setPreview] = useState<{ roomId: string; works: ViewingRoomWork[] } | null>(
    null,
  );
  const previewImages = useWorkThumbnails(preview ? preview.works : null, (work) => work);

  const copyTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const reload = useCallback(() => setNonce((value) => value + 1), []);

  // The rooms themselves. Skipped entirely until there is a handle, because
  // every row is a link and there is no address for one to be at.
  useEffect(() => {
    if (handle === null) return;
    let active = true;

    fetchViewingRooms()
      .then((rows) => {
        if (active) setRooms(rows);
      })
      .catch((cause: unknown) => {
        if (active) setFailure(describeError(cause));
      });

    return () => {
      active = false;
    };
  }, [handle, nonce]);

  // The visit list, lazily and on the room's own screen only. The list rows
  // carry a count already, so pulling every room's visitors to render a number
  // the server has sent would be one request per row.
  //
  // Stored ALONGSIDE the room it belongs to rather than cleared when the record
  // changes — the shape `useMe` uses. Clearing it would be a setState in an
  // effect body, and worse, a response for the previous room could land after
  // the clear and render against the new one.
  useEffect(() => {
    const roomId = input.record;
    if (roomId === null) return;

    let active = true;

    fetchViewingRoomVisitors(roomId)
      .then((rows) => {
        if (active) setVisits({ roomId, rows });
      })
      // A room whose visit list will not load still shows its link and its
      // controls. Losing the log is worse than a blank list; losing the screen
      // is worse than both.
      .catch(() => {
        if (active) setVisits({ roomId, rows: [] });
      });

    return () => {
      active = false;
    };
  }, [input.record, nonce]);

  // What may be published, loaded only when the form asks for it, so the list
  // screen never pays for a fan-out it does not render.
  useEffect(() => {
    if (stage.kind !== "form" || collections !== null) return;
    let active = true;

    fetchCollections()
      .then((rows) => {
        if (active) setCollections(rows);
      })
      .catch((cause: unknown) => {
        if (active) setError(describeError(cause));
      });

    return () => {
      active = false;
    };
  }, [stage.kind, collections]);

  // There is no second fetch here any more, and its absence is the feature.
  //
  // This used to fan out over every owned collection to pull every work into
  // memory, so the form could offer them as chips — the N+1 the comment on it
  // called out, with the fix it named ("a batched works endpoint behind
  // ondo.works_in()"). That endpoint now exists as GET /v1/works/publishable,
  // and `useWorkPickerController` asks it for one page at a time. Opening the
  // form costs one request for the collections, whatever the collector owns.
  //
  // The collections fetch above stays: `SourceView.nothing` still needs it to
  // say "you do not own a collection yet, so there is nothing to publish",
  // which is true of the works kind as much as the collections kind.

  /**
   * What the room actually holds, for the preview.
   *
   * Wanted on the room's own screen and on the edit form, which is why the id
   * is derived rather than taken from `input.record` alone. Stored WITH that id
   * for the reason `visits` is: a response for the room the reader has left
   * must not render against the one they are on.
   */
  const previewRoomId =
    stage.kind === "form" && stage.mode.kind === "edit" ? stage.mode.roomId : input.record;

  useEffect(() => {
    if (previewRoomId === null) return;
    let active = true;

    fetchViewingRoomWorks(previewRoomId)
      .then((works) => {
        if (active) setPreview({ roomId: previewRoomId, works });
      })
      // A preview that will not load costs a glance, not the screen.
      .catch(() => {
        if (active) setPreview({ roomId: previewRoomId, works: [] });
      });

    return () => {
      active = false;
    };
  }, [previewRoomId, nonce]);

  useEffect(
    () => () => {
      if (copyTimer.current) clearTimeout(copyTimer.current);
    },
    [],
  );

  const run = (action: Promise<unknown>, after?: () => void) => {
    setBusy(true);
    setError(null);
    action
      .then(() => {
        after?.();
        reload();
      })
      .catch((cause: unknown) => setError(describeError(cause)))
      .finally(() => setBusy(false));
  };

  const draft = stage.kind === "form" ? stage.draft : EMPTY_DRAFT;
  const source = toSourceView(draft, collections);

  return {
    view: resolveRooms({
      handle,
      handleDraft,
      handleProblem,
      rooms,
      failure,
      record: input.record,
      stage,
      source,
      problem,
      // Derived, so a list belonging to a room the reader has left can never be
      // shown against the one they are on.
      visits: visits?.roomId === input.record ? visits.rows : null,
      preview:
        preview?.roomId === previewRoomId
          ? toPreview(preview.works, previewImages, PREVIEW_LIMIT)
          : null,
      busy,
      error,
      copiedId,
      confirmingId,
      origin: input.origin,
      roomsHref: input.roomsHref,
    }),

    typeHandle: (value) => {
      setHandleDraft(value);
      setHandleProblem(null);
    },

    claimHandle: () => {
      const invalid = validateHandle(handleDraft);
      if (invalid) {
        setHandleProblem(invalid);
        return;
      }

      setBusy(true);
      setMyHandle({ handle: normaliseHandle(handleDraft) })
        .then((result) => setHandle(result.handle))
        // The server answers malformed, reserved and taken identically on
        // purpose, so this shows what it said rather than guessing which.
        .catch((cause: unknown) =>
          setHandleProblem(
            cause instanceof Error && cause.message.trim() !== ""
              ? cause.message
              : VIEWING_ROOMS_COPY.handleUnavailable,
          ),
        )
        .finally(() => setBusy(false));
    },

    startCreating: () => {
      setProblem(null);
      setError(null);
      setStage({ kind: "form", mode: { kind: "create" }, draft: EMPTY_DRAFT, picking: false });
    },

    /**
     * Opens the form over a room that already exists.
     *
     * The selection is loaded BEFORE the form appears rather than filled in
     * afterwards. A form that opened with nothing ticked and then quietly
     * populated is one somebody can save mid-flight — and saving a works room
     * whose picker had not caught up would empty it.
     *
     * The works come from the room's own preview, which resolves through
     * `ondo.room_work_ids()` — so a work that has since left the collection it
     * was in is already gone from the list rather than being offered back and
     * then refused on save.
     */
    startEditing: (roomId) => {
      const room = rooms?.find((each) => each.id === roomId);
      if (!room) return;

      setProblem(null);
      setError(null);
      setBusy(true);

      const collectionsFor = (rows: CollectionSummary[]) =>
        room.collectionIds.flatMap((id) => {
          const found = rows.find((each) => each.id === id);
          // A collection the room names but the caller no longer owns. Dropped
          // rather than shown as an unlabelled tick: it resolves to nothing on
          // the public page already, and saving without it is the honest result.
          return found ? [{ key: found.id, label: found.name }] : [];
        });

      const needed = Promise.all([
        collections ? Promise.resolve(collections) : fetchCollections().then((rows) => {
          setCollections(rows);
          return rows;
        }),
        room.scope === "works" ? fetchViewingRoomWorks(roomId) : Promise.resolve([]),
      ]);

      needed
        .then(([collectionRows, works]) => {
          setStage({
            kind: "form",
            mode: { kind: "edit", roomId },
            draft: draftFromRoom(room, {
              collections: collectionsFor(collectionRows),
              works: works.map((work) => ({ key: work.id, label: labelFor(work) })),
            }),
            picking: false,
          });
        })
        .catch((cause: unknown) => setError(describeError(cause)))
        .finally(() => setBusy(false));
    },

    editDraft: (patch) => {
      setProblem(null);
      setStage((current) =>
        current.kind === "form"
          ? { ...current, draft: { ...current.draft, ...patch } }
          : current,
      );
    },

    // The tick list is cleared with the kind, so a set of collection ids can
    // never be sent as a set of work ids. That rule lives in `withSourceKind`
    // rather than here, because in the model it can be tested.
    chooseSource: (kind) => {
      setProblem(null);
      setStage((current) =>
        current.kind === "form"
          ? {
              ...current,
              draft: withSourceKind(current.draft, kind),
              // Switching away from works closes the picker. Leaving it open
              // over a list that no longer governs the room is worse than the
              // extra line here.
              picking: current.picking && kind === "works",
            }
          : current,
      );
    },

    toggleChosen: (choice) => {
      setProblem(null);
      setStage((current) =>
        current.kind === "form"
          ? {
              ...current,
              draft: { ...current.draft, chosen: toggleChoice(current.draft.chosen, choice) },
            }
          : current,
      );
    },

    submitDraft: () => {
      if (stage.kind !== "form") return;

      const invalid = validateDraft(stage.draft);
      if (invalid) {
        setProblem(invalid);
        return;
      }

      const mode = stage.mode;
      setBusy(true);
      setError(null);

      if (mode.kind === "edit") {
        updateViewingRoom(mode.roomId, toUpdateRequest(stage.draft))
          .then(() => {
            // Back to the room, not to the list: the URL already names it, and
            // the reader was looking at it a moment ago. A refusal leaves the
            // stage alone, so a rejected save keeps the form and everything in
            // it rather than throwing the edit away.
            setStage({ kind: "browsing" });
            reload();
          })
          .catch((cause: unknown) => setError(describeError(cause)))
          .finally(() => setBusy(false));
        return;
      }

      createViewingRoom(toCreateRequest(stage.draft))
        .then((room) => {
          setStage({ kind: "created", roomId: room.id });
          reload();
        })
        .catch((cause: unknown) => setError(describeError(cause)))
        .finally(() => setBusy(false));
    },

    startPicking: () => {
      setProblem(null);
      setStage((current) => (current.kind === "form" ? { ...current, picking: true } : current));
    },

    stopPicking: () => {
      setStage((current) => (current.kind === "form" ? { ...current, picking: false } : current));
    },

    cancelCreating: () => {
      setProblem(null);
      setStage({ kind: "browsing" });
    },

    copyLink: (roomId, url) => {
      if (copyTimer.current) clearTimeout(copyTimer.current);

      // No document.execCommand fallback. A browser that refuses the clipboard
      // is telling the user something, and the address is on screen to select.
      //
      // `?.` AND NOT A BARE READ, because `navigator.clipboard` is undefined
      // rather than failing: the API is gated on a secure context, so any
      // origin that is not https or localhost — the app served over plain http
      // on a LAN address, which is how it gets demonstrated — has no
      // `clipboard` at all. A bare `navigator.clipboard.writeText` throws
      // SYNCHRONOUSLY on the property access, which `.catch` never sees, so the
      // reader got an uncaught TypeError and `copyFailed` never rendered — the
      // one sentence written for exactly this case.
      const written = navigator.clipboard?.writeText(url);

      if (!written) {
        setError(VIEWING_ROOMS_COPY.copyFailed);
        return;
      }

      written
        .then(() => {
          setCopiedId(roomId);
          copyTimer.current = setTimeout(() => setCopiedId(null), COPIED_FOR_MS);
        })
        .catch(() => setError(VIEWING_ROOMS_COPY.copyFailed));
    },

    hideRoom: (roomId, hidden) => {
      run(setViewingRoomHidden(roomId, hidden));
    },

    askToDelete: (roomId) => {
      setError(null);
      setConfirmingId(roomId);
    },

    // Two presses, because it takes the visit log with it and the link cannot
    // be brought back.
    deleteRoom: (roomId) => {
      run(deleteViewingRoom(roomId), () => {
        setConfirmingId(null);
        setStage({ kind: "browsing" });
      });
    },

  };
}

/**
 * The picker's options for whichever kind the draft is on.
 *
 * `nothing` and `loading` are different answers: one says the collections have
 * not arrived, the other says this account owns no collection and there is
 * nothing a room could be built over. A spinner for the second would never
 * resolve.
 *
 * The `works` kind has no options here at all. It offers a button that opens
 * `WorkPickerDialog`, which does its own searching and paging — a flat list of
 * every work was the thing this replaced. But it still needs `collections` for
 * the `nothing` case, because owning no collection means owning no publishable
 * work either, and a picker that opened onto "no results" would be telling
 * somebody to refine a search that cannot succeed.
 */
function toSourceView(
  draft: RoomDraft,
  collections: CollectionSummary[] | null,
): SourceView {
  const base = { kind: draft.sourceKind, chosen: draft.chosen };

  if (draft.sourceKind === "all") {
    return { ...base, options: [], loading: false, nothing: null };
  }

  if (!collections) {
    return { ...base, options: [], loading: true, nothing: null };
  }

  const publishable = roomCollectionOptions(collections);
  if (publishable.length === 0) {
    return {
      ...base,
      options: [],
      loading: false,
      nothing: VIEWING_ROOMS_COPY.sourceNothing,
    };
  }

  if (draft.sourceKind === "collections") {
    return { ...base, options: publishable, loading: false, nothing: null };
  }

  // `works`: no options, by design. The dialog fetches its own.
  return { ...base, options: [], loading: false, nothing: null };
}
